import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { Operator, SiteSummary } from '../../api/types';
import {
  useMe,
  useMyPermissions,
  useOperatorsList,
  useSiteAction,
  useSitesList,
} from '../../api/queries';
import { SiteFormDialog } from './SiteFormDialog';
import { SiteDetailDialog } from './SiteDetailDialog';
import type { SiteTab } from './SiteDetailDialog';

export default function SitesPage() {
  usePageTitle('Sites');
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  // ACES users can scope by operator; operator-tenant users see only their own.
  const [operatorFilter, setOperatorFilter] = useState<string>('');
  const operatorIdForList = isArms ? (operatorFilter || null) : (me.data?.operator_id ?? null);
  const { queryKey, listFn } = useSitesList(operatorIdForList);

  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms,
  });
  const operatorById = useMemo(() => {
    const m = new Map<string, Operator>();
    for (const o of operatorsQ.data ?? []) m.set(o.id, o);
    return m;
  }, [operatorsQ.data]);

  const [detail, setDetail] = useState<{ site: SiteSummary; tab: SiteTab } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<SiteSummary | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{
    site: SiteSummary; action: 'disable' | 'reactivate';
  } | null>(null);
  const siteAction = useSiteAction(
    confirmTarget?.site.id ?? '00000000-0000-0000-0000-000000000000',
  );

  const columns = useMemo<GridColDef<SiteSummary>[]>(() => [
    { field: 'displayName', headerName: 'Site', flex: 1.5,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.displayName}</Typography>
          <Typography variant="caption" color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}>{p.row.code}</Typography>
        </Stack>
      ) },
    ...(isArms ? [{
      field: 'operatorId', headerName: 'Operator', width: 200,
      renderCell: (p: { row: SiteSummary }) => {
        const op = operatorById.get(p.row.operatorId);
        return op ? `${op.displayName} (${op.code})` : p.row.operatorId.slice(0, 8) + '…';
      },
    } as GridColDef<SiteSummary>] : []),
    { field: 'city', headerName: 'City', width: 160,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.city}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[p.row.stateProvince, p.row.postalCode].filter(Boolean).join(' ') || '—'}
          </Typography>
        </Stack>
      ) },
    { field: 'timeZone', headerName: 'Time zone', width: 160 },
    // Release channel — sites flipped to 'beta' get pre-release
    // sc-runtime updates. Default 'stable'.
    { field: 'releaseChannel', headerName: 'Release channel', width: 150,
      renderCell: (p) => <ReleaseChannelChip channel={p.row.releaseChannel} /> },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusChip status={p.row.status} /> },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], [isArms, operatorById]);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'displayName', label: 'Site name' },
    { kind: 'text', key: 'code',        label: 'Code' },
    { kind: 'text', key: 'city',        label: 'City' },
    { kind: 'select', key: 'status',    label: 'Status', options: [
      { value: 'active',   label: 'Active' },
      { value: 'disabled', label: 'Disabled' },
    ] },
    { kind: 'select', key: 'releaseChannel', label: 'Release channel', options: [
      { value: 'stable', label: 'Stable' },
      { value: 'beta',   label: 'Beta' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<SiteSummary>[]>(() => [
    { label: 'All',      filter: () => true },
    { label: 'Active',   filter: (r) => r.status === 'active' },
    { label: 'Disabled', filter: (r) => r.status === 'disabled' },
    { label: 'Beta channel', filter: (r) => r.releaseChannel === 'beta' },
  ], []);

  const exportColumns = useMemo<ExportColumn<SiteSummary>[]>(() => [
    { key: 'code',                 label: 'Code' },
    { key: 'displayName',          label: 'Display Name' },
    { key: 'operatorId',           label: 'Operator',
      format: (v) => operatorById.get(String(v))?.displayName ?? String(v) },
    { key: 'addressLine1',         label: 'Address' },
    { key: 'city',                 label: 'City' },
    { key: 'stateProvince',        label: 'State / Province', format: (v) => fmtOrEmDash(v) },
    { key: 'postalCode',           label: 'Postal Code',     format: (v) => fmtOrEmDash(v) },
    { key: 'timeZone',             label: 'Time Zone' },
    { key: 'releaseChannel',       label: 'Release Channel' },
    { key: 'gamingDayOffsetMinutes', label: 'Gaming Day Offset', format: (v) => fmtGamingDayOffset(Number(v)) },
    { key: 'status',               label: 'Status' },
    { key: 'createdAt',            label: 'Created', format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',            label: 'Updated', format: (v) => fmtDateTime(v) },
  ], [operatorById]);

  const actions = useMemo<RowAction<SiteSummary>[]>(() => [
    { label: 'View', onClick: (row) => setDetail({ site: row, tab: 'details' }) },
    {
      label: 'Edit',
      visible: () => has('sites.edit'),
      onClick: (row) => setEditRow(row),
    },
    // Cash-desk deep links (owner request 2026-08-23): straight to the
    // site-scoped tab instead of the picker on the standalone pages.
    {
      label: 'Cash desk configuration',
      visible: () => has('cashdesk.options.manage'),
      onClick: (row) => setDetail({ site: row, tab: 'cashdesk' }),
    },
    {
      label: 'Cash desk layout',
      visible: () => has('cashdesk.layout.manage'),
      onClick: (row) => setDetail({ site: row, tab: 'layout' }),
    },
    {
      label: 'Cash desk terminals',
      visible: () => has('cashdesk.terminals.manage'),
      onClick: (row) => setDetail({ site: row, tab: 'terminals' }),
    },
    {
      label: 'Cash desk shifts',
      visible: () => has('cashdesk.activity.read'),
      onClick: (row) => setDetail({ site: row, tab: 'shifts' }),
    },
    {
      label: 'Disable',
      destructive: true,
      visible: (row) => has('sites.admin') && row.status === 'active',
      onClick: (row) => setConfirmTarget({ site: row, action: 'disable' }),
    },
    {
      label: 'Reactivate',
      visible: (row) => has('sites.admin') && row.status === 'disabled',
      onClick: (row) => setConfirmTarget({ site: row, action: 'reactivate' }),
    },
  ], [has]);

  return (
    <>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="h4" sx={{ flex: 1 }}>Sites</Typography>
          {isArms && (
            <TextField
              select
              size="small"
              label="Operator"
              value={operatorFilter}
              onChange={(e) => setOperatorFilter(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">All operators</MenuItem>
              {(operatorsQ.data ?? []).map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.displayName} ({op.code})
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <RouteDataGrid<SiteSummary>
          entityName="Sites"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Sites"
          actions={actions}
          primaryAction={
            has('sites.edit')
              ? { label: '+ Create site', onClick: () => setCreateOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetail({ site: row, tab: 'details' })}
          emptyMessage={isArms && !operatorFilter
            ? 'No sites yet. Pick an operator or create one.'
            : 'No sites for this operator yet.'}
        />

        <SiteDetailDialog
          site={detail?.site ?? null}
          initialTab={detail?.tab ?? 'details'}
          operatorById={operatorById}
          onClose={() => setDetail(null)}
        />

        <SiteFormDialog
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
        />

        <SiteFormDialog
          open={editRow !== null}
          mode="edit"
          site={editRow ?? undefined}
          onClose={() => setEditRow(null)}
        />

        <ConfirmDialog
          open={confirmTarget !== null}
          title={confirmTarget?.action === 'disable' ? 'Disable site' : 'Reactivate site'}
          message={confirmTarget ? (
            confirmTarget.action === 'disable'
              ? <>Disable site <strong>{confirmTarget.site.displayName}</strong>? Machines on the site will be marked offline; the site controller will refuse new sessions until reactivated.</>
              : <>Reactivate site <strong>{confirmTarget.site.displayName}</strong>? The site controller will accept sessions again.</>
          ) : ''}
          confirmLabel={confirmTarget?.action === 'disable' ? 'Disable' : 'Reactivate'}
          destructive={confirmTarget?.action === 'disable'}
          busy={siteAction.isPending}
          error={(siteAction.error as { message?: string } | null)?.message}
          onConfirm={() => {
            if (!confirmTarget) return;
            siteAction.mutate(confirmTarget.action, {
              onSuccess: () => setConfirmTarget(null),
            });
          }}
          onCancel={() => setConfirmTarget(null)}
        />
      </Stack>
    </>
  );
}

function StatusChip({ status }: { status: string }) {
  const color = status === 'active'   ? 'success'
              : status === 'disabled' ? 'default'
              : 'default';
  return <Chip size="small" label={status} color={color as 'success' | 'default'} />;
}

// Renders the raw minutes-after-midnight offset alongside the wall-clock
// time the gaming day starts, e.g. `360 (06:00)`. 0 = calendar day.
function fmtGamingDayOffset(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${minutes} (${h}:${m})`;
}

function ReleaseChannelChip({ channel }: { channel: string }) {
  // 'beta' is visually loud so a tech notices a site that's been
  // promoted to test releases; 'stable' is the safe default.
  return channel === 'beta'
    ? <Chip size="small" label="beta" color="warning" />
    : <Chip size="small" label="stable" color="default" variant="outlined" />;
}
